import { Flex } from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Country } from 'react-phone-number-input';
import { Grid } from '@material-ui/core';

import useControllers from './useControllers';
import BusinessContactDetailsSection from '../BusinessContactDetailsSection';
import SupportContactDetailsSection from '../SupportContactDetailsSection';
import { ContactDetailsFormValues } from '../../types';
import {
  isBusinessContactEmailRequired,
  isBusinessContactEmailVisible,
} from '../../utils';

interface ContactDetailsFormProps {
  entityType?: string;
  defaultValues?: ContactDetailsFormValues;
  onChange?: (values: ContactDetailsFormValues) => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

const ContactDetailsForm: FunctionComponent<ContactDetailsFormProps> = ({
  entityType = '',
  defaultValues,
  onChange,
  onPrevious,
  onNext,
}) => {
  const {
    control,
    formState: { isSubmitting, errors },
    watch,
    handleSubmit,
  } = useForm<ContactDetailsFormValues>({
    mode: 'onTouched',
    defaultValues,
  });
  const [phoneCountry, setPhoneCountry] = useState<Country>();
  const {
    email,
    setEmail,
    validateEmail,
    emailError,
    phone,
    setPhone,
    validatePhone,
    phoneError,
    businessContactEmail,
    setBusinessContactEmail,
    validateBusinessContactEmail,
    businessContactEmailError,
  } = useControllers(control, entityType, phoneCountry);

  const handleValid = () => {
    onNext?.();
  };

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      onChange?.(value as ContactDetailsFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Grid
        className="csp-portal"
        container
        spacing={0}
        justifyContent="flex-start"
      >
        <Grid item lg={12}>
          <div className="csp-verification-form brands">
            <Flex
              sx={{
                flexDirection: 'column',
                rowGap: '30px',
                marginBottom: '30px',
              }}
            >
              <SupportContactDetailsSection
                email={email}
                emailError={emailError?.message}
                phone={phone}
                phoneError={phoneError?.message}
                onEmailChange={(value) => setEmail(value)}
                onEmailBlur={validateEmail}
                onPhoneCountryChange={(value) => setPhoneCountry(value)}
                onPhoneChange={(value) => setPhone(value)}
                onPhoneBlur={validatePhone}
              />
              {isBusinessContactEmailVisible(entityType) && (
                <BusinessContactDetailsSection
                  required={isBusinessContactEmailRequired(entityType)}
                  entityType={entityType}
                  email={businessContactEmail}
                  emailError={businessContactEmailError?.message}
                  onEmailChange={(value) => setBusinessContactEmail(value)}
                  onEmailBlur={validateBusinessContactEmail}
                />
              )}
            </Flex>
          </div>
        </Grid>
        <Grid item xs={12} className="bottom-blk">
          <ul className="list-inline">
            <li>
              <p>
                <a
                  onClick={onPrevious}
                  className={
                    isSubmitting ? 'secondary-btn disabled' : 'secondary-btn'
                  }
                >
                  Previous
                </a>
              </p>
            </li>
            <li className="form-group-field">
              <input
                type="submit"
                disabled={
                  isSubmitting ||
                  Object.values(errors).some((error) => !!error.message)
                }
                value={isSubmitting ? 'Processing...' : 'Next'}
              />
            </li>
          </ul>
        </Grid>
      </Grid>
    </form>
  );
};

export default ContactDetailsForm;
